import AddchartIcon from '@mui/icons-material/Addchart';
import AddCircleOutlinedIcon from '@mui/icons-material/AddCircleOutlined';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Grid, Stack, List, ListItem, ListItemText } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import {
  IComposableDashboard,
  composableDashboardFiltersLabelValue,
  ComposableDashboardFiltersEnum,
  IComposableDashboardCollection,
  saveDashboardCollection,
  IComposableDashboardCollectionsContext,
  updateDashboardCollection,
} from 'wisestep-api';
import { WsButton, WsCard, CardVariant, WsColors, WsDrawer, WsPageHeader } from 'wisestep-base';

import { WsBreadCrumbs } from '../../../../components/WsBreadCrumbs';
import { useWorkspaceContext } from '../../../../context/WorkspaceContext/WorkspaceContext';
import {
  getDashBoardBreadCrumbList,
  IDashBoardBreadCrumbTypes,
} from '../../../../features/users/helper/defaultValues';
import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { useComposableDashboardCollectionsContext } from '../../hooks/useComposableDashboardCollectionsContext';
import { useComposableDashboardsFiltersContext } from '../../hooks/useComposableDashboardsFiltersContext';
import { IAnalyticsDashboardViewer } from '../dashboard/AnalyticsDashboardViewer';
import RenderDashboardsTabs from '../dashboard/RenderDashboardsTabs';
import StyledButton from '../StyledButton';

import AddDashboardsSelectionDrawer from './AddDashboardsSelectionDrawer';
import SaveCollectionDialog from './SaveCollectionDialog';

const DashboardCollectionViewer = ({ requireTitleBanner = true }: IAnalyticsDashboardViewer) => {
  const { selectedDashboardCollection, setSelectedCollection, setDashboardCollections } =
    useComposableDashboardCollectionsContext() as Required<IComposableDashboardCollectionsContext>;
  const setSelectedDashboards = (dashboards: IComposableDashboard[]) =>
    setSelectedCollection((prev) => ({
      ...prev,
      dashboardIds: dashboards?.map(({ id }) => id ?? ''),
      dashboards,
    }));
  const [openSaveModal, setSaveModal] = useState(false);
  const forUpdate = !isEmpty(selectedDashboardCollection?.id);
  const [viewMode, setViewMode] = useState(forUpdate ? true : false);
  const setNotification = useNotificationStore((state) => state.setNotification);

  const [openFilterSelectionDrawer, setOpenFilterSelectionDrawer] = useState(false);

  const [openDashboardsSelectionDrawer, setOpenDashboardsSelectionDrawer] =
    useState<boolean>(false);

  const { addFilterToDashboard, removeFilterFromDashboard, filtersAdddedToDashboard } =
    useComposableDashboardsFiltersContext();

  const onPreviewDashboards = () => setOpenDashboardsSelectionDrawer(false);
  const workspaceContext = useWorkspaceContext();

  const saveCollection = async (newDashboardCollection: IComposableDashboardCollection) => {
    if (forUpdate) {
      try {
        const response = await updateDashboardCollection(newDashboardCollection);
        setDashboardCollections((prev) => {
          prev.splice(
            prev.findIndex((collection) => collection.id === newDashboardCollection.id),
            1,
            response.data
          );
          return [...prev];
        });
        setNotification(success('Updated Dashboard Collection Successfully!'));
      } catch (e) {
        setNotification(failed('Some error occurred. Please try later'));
      }
    } else {
      try {
        const response = await saveDashboardCollection(newDashboardCollection);
        setDashboardCollections((prev) => {
          prev.push(response.data);
          return [...prev];
        });
        setSelectedCollection((prev) => ({
          ...prev,
          name: response.data.name,
        }));
        setNotification(success('New Dashboard Collection Created Successfully!'));
      } catch (e) {
        setNotification(failed('Some error occurred. Please try later'));
      }
    }
    setSaveModal(false);
  };

  const AddNewDashboardCollectionMesg = () => (
    <Grid mt={4}>
      <WsCard
        cardType={CardVariant.WITH_OUT_BG}
        title="Compose Your Dashboards Collection"
        description="Start adding dashboards"
        middleComponent={<AddchartIcon sx={{ color: WsColors.TheBlues500, fontSize: 100 }} />}
        isButton={true}
        buttonText="Add Dashboards Collection"
        btnStartIcon={<AddchartIcon />}
        onClick={() => {
          setOpenDashboardsSelectionDrawer(true);
          setViewMode(false);
        }}
        buttonSize="large"
        sx={{ height: '70vh', p: '10vw' }}
      />
    </Grid>
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} pb={2}>
        {requireTitleBanner && (
          <>
            <WsBreadCrumbs
              breadCrumbsList={getDashBoardBreadCrumbList(
                IDashBoardBreadCrumbTypes.MASTER_DASHBOARDS,
                !isEmpty(selectedDashboardCollection?.name)
                  ? selectedDashboardCollection?.name
                  : 'Create Master DashBoard'
              )}
            />
            <Grid item xs={12} pb={2}>
              <WsPageHeader
                title={selectedDashboardCollection?.name ?? 'Title not defined'}
                rightChildren={
                  viewMode && (
                    <WsButton
                      disabled={
                        selectedDashboardCollection.createdBy == 'Default' && !workspaceContext.owns
                          ? true
                          : false
                      }
                      onClick={() => {
                        setOpenDashboardsSelectionDrawer(true);
                        setViewMode(false);
                      }}
                    >
                      Edit
                    </WsButton>
                  )
                }
              />
            </Grid>
          </>
        )}
        {!viewMode && (
          <Stack direction="row" p={2} justifyContent="end" spacing={2}>
            <WsButton onClick={() => setSaveModal(true)}>Save</WsButton>
            <WsButton onClick={() => setViewMode(true)}>Cancel</WsButton>
            <WsButton onClick={() => setOpenFilterSelectionDrawer(true)}>Add Filters</WsButton>
            <WsButton onClick={() => setOpenDashboardsSelectionDrawer(true)}>
              Add Dashboards
            </WsButton>
          </Stack>
        )}
        {!isEmpty(selectedDashboardCollection.dashboards) ? (
          <RenderDashboardsTabs />
        ) : (
          <AddNewDashboardCollectionMesg />
        )}
      </Grid>

      {openDashboardsSelectionDrawer && (
        <AddDashboardsSelectionDrawer
          openSelectionDrawer={openDashboardsSelectionDrawer}
          setChartSelectionDrawer={setOpenDashboardsSelectionDrawer}
          onPreview={onPreviewDashboards}
          selectedDashboards={selectedDashboardCollection?.dashboards ?? []}
          setSelectedDashboards={setSelectedDashboards}
        />
      )}
      {openFilterSelectionDrawer && (
        <WsDrawer
          open={openFilterSelectionDrawer}
          onClose={() => setOpenFilterSelectionDrawer(false)}
          size="small"
        >
          <List sx={{ width: '100%', bgcolor: 'background.paper', px: 4, py: 2 }}>
            {Object.keys(composableDashboardFiltersLabelValue).map((filterEnum) => (
              <ListItem
                key={filterEnum}
                disableGutters
                sx={{ borderBottom: '1px solid black', marginTop: '2px' }}
                secondaryAction={
                  <>
                    {(filtersAdddedToDashboard as ComposableDashboardFiltersEnum[]).filter(
                      (addedFilter) => addedFilter === filterEnum
                    ).length > 0 ? (
                      <StyledButton
                        variant="text"
                        size="small"
                        startIcon={<RemoveCircleIcon />}
                        remove={true}
                        onClick={() =>
                          removeFilterFromDashboard &&
                          removeFilterFromDashboard(filterEnum as ComposableDashboardFiltersEnum)
                        }
                      >
                        Remove
                      </StyledButton>
                    ) : (
                      <StyledButton
                        variant="text"
                        size="small"
                        startIcon={<AddCircleOutlinedIcon />}
                        remove={false}
                        onClick={() =>
                          addFilterToDashboard &&
                          addFilterToDashboard(filterEnum as ComposableDashboardFiltersEnum)
                        }
                      >
                        Add
                      </StyledButton>
                    )}
                  </>
                }
              >
                <ListItemText primary={composableDashboardFiltersLabelValue[filterEnum].label} />
              </ListItem>
            ))}
          </List>
        </WsDrawer>
      )}
      {openSaveModal && (
        <SaveCollectionDialog
          saveCollection={saveCollection}
          setSaveModal={setSaveModal}
          update={forUpdate}
        />
      )}
    </Grid>
  );
};
export default DashboardCollectionViewer;
